import { useAuftragRepository } from '@Hooks/useRepository';
import { EnumContainerActionStatus } from '@Model';
import { OrdersRepository } from '@Repositories/Auftragsbearbeitung';
import {
    EnumDirection,
    EnumFilterExpressionOperation,
    EnumOperation,
    isOk,
    type ListRequest,
} from '@synqo/frontend-component-library';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { type ContainerHistoryEntry, HISTORY_LENGTH, toHistoryEntries } from './containerHistory';

export interface ContainerHistoryRequest {
    siteId: string | undefined;
    customerId: string | undefined;
    bookedContainers: number;
    savedAt?: string;
}

export interface ContainerHistoryState {
    entries: ContainerHistoryEntry[];
    failed: boolean;
}

const NO_ENTRIES: ContainerHistoryEntry[] = [];

const newestOrdersWithBookedContainers = (siteId: string, customerId: string): ListRequest => ({
    page_no: 0,
    page_size: HISTORY_LENGTH,
    full_text_search: '',
    sort_columns: [{ column_name: 'completed_at', direction: EnumDirection.DESC }],
    filter_columns: {
        operation: EnumFilterExpressionOperation.And,
        filters: [
            { field: 'customer.foreign_key', value: customerId, operator: EnumOperation.Equal },
            { field: 'site.foreign_key', value: siteId, operator: EnumOperation.Equal },
            {
                field: 'container_actions.booking_status',
                value: EnumContainerActionStatus.Booked.toString(),
                operator: EnumOperation.Equal,
            },
        ],
    },
});

/**
 * The latest booked container actions of a customer at a site, read from as many of the newest orders that booked
 * any as the history has lines, since each order gives at least one.
 */
// @ai-generated 2026-10-21T06:34:12+00:00 by malte.ulfers@tngtech.com using Claude Opus 5.5 (1M context)
export function useContainerHistory({
    siteId,
    customerId,
    bookedContainers,
    savedAt,
}: ContainerHistoryRequest): ContainerHistoryState {
    const orders = useAuftragRepository(OrdersRepository);
    const canBeRead = !!siteId && !!customerId;

    const { data } = useQuery({
        queryKey: ['container-history', siteId, customerId, bookedContainers, savedAt],
        queryFn: () =>
            orders.getListPost(newestOrdersWithBookedContainers(siteId ?? '', customerId ?? ''), 'ContainerActions'),
        enabled: canBeRead,
        placeholderData: (previous, previousQuery) => {
            const sameSite = previousQuery?.queryKey[1] === siteId && previousQuery?.queryKey[2] === customerId;

            return sameSite ? previous : undefined;
        },
    });

    const entries = useMemo(
        () => (data !== undefined && isOk(data) ? toHistoryEntries(data.data.items) : NO_ENTRIES),
        [data],
    );

    if (!canBeRead || data === undefined) {
        return { entries: NO_ENTRIES, failed: false };
    }

    return isOk(data) ? { entries, failed: false } : { entries: NO_ENTRIES, failed: true };
}
